import { useState } from "react";
import { Controller } from "react-hook-form";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { Modal } from "@/components/modal";
import { Form, FormField, FormSubmit } from "@/src/forms";
import { ACCOUNT_TYPE_LABELS, getAssignableRoles, type AccountType } from "../_data";
import { SelectField } from "./select-field";
import { inviteMemberSchema, type InviteMemberValues } from "../_schemas/invite-member";
import { inviteUser } from "@/src/services/company_service";

const MONITORING_OPTIONS = [
  { value: "disabled", label: "Disabled" },
  { value: "enabled", label: "Enabled" },
];

type Props = {
  open: boolean;
  onClose: () => void;
  /** Fired after the backend creates the invite; the parent reloads the list. */
  onCreated: () => void;
  /** The inviter's own role — actions are descending-only (e.g. an Admin can't invite another Admin). */
  actorRole: AccountType;
};

export function InviteMemberModal({ open, onClose, onCreated, actorRole }: Props) {
  const [inviteError, setInviteError] = useState<string | null>(null);
  const roleOptions = getAssignableRoles(actorRole).map((t) => ({
    value: t,
    label: ACCOUNT_TYPE_LABELS[t],
  }));

  function handleClose() {
    setInviteError(null);
    onClose();
  }

  async function handleValid(values: InviteMemberValues) {
    setInviteError(null);
    const res = await inviteUser({
      email: values.email.trim(),
      accountType: values.role,
      enableMonitoring: values.monitoring === "enabled",
    });
    if (!res.ok) {
      setInviteError(res.error.message);
      return;
    }
    onCreated();
    handleClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Invite member"
      description="They'll receive an email with a one-time login code."
    >
      <Form
        schema={inviteMemberSchema}
        defaultValues={{ email: "", role: "", monitoring: "disabled" }}
        onSubmit={handleValid}
        className="flex flex-col gap-4"
      >
        {inviteError && (
          <Alert tone="danger" role="alert">
            {inviteError}
          </Alert>
        )}
        <FormField
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="name@company.com"
        />
        <Controller
          name="role"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1">
              <SelectField
                label="Role"
                value={field.value || null}
                options={roleOptions}
                onChange={field.onChange}
                placeholder="Select role"
              />
              {fieldState.error && (
                <p className="text-small text-red-600">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />
        <Controller
          name="monitoring"
          render={({ field }) => (
            <SelectField
              label="Monitoring"
              value={field.value}
              options={MONITORING_OPTIONS}
              onChange={field.onChange}
            />
          )}
        />
        <div className="flex justify-end gap-2">
          <Button hierarchy="secondary" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <FormSubmit
            hierarchy="primary"
            pendingLabel="Sending…"
            rightIcon={<Icon name="arrow_forward" size={20} />}
          >
            Send invite
          </FormSubmit>
        </div>
      </Form>
    </Modal>
  );
}
